import type { CSSProperties } from 'react';
import { monthLabel } from '@fleet/shared';
import { Dropdown } from './Dropdown';
import type { DropdownOption } from './Dropdown';

const chipStyle = (on: boolean): CSSProperties => ({
  background: on ? '#2A6085' : '#FFFFFF',
  color: on ? '#FFFFFF' : '#2A6085',
  border: `1px solid ${on ? '#2A6085' : '#C9BBA8'}`,
  borderRadius: 2,
  padding: '5px 12px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Roboto, sans-serif',
});

/** Month options grouped by year, newest year first. */
function monthOptions(months: string[]): DropdownOption[] {
  const groups: Record<string, string[]> = {};
  for (const m of months) (groups[m.slice(0, 4)] ??= []).push(m);
  return Object.entries(groups)
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .flatMap(([year, ms]) => ms.map((m) => ({ value: m, label: monthLabel(m), group: year })));
}

/**
 * Which period a whole section reports on.
 *
 * It used to live inside the per-person panel, which made it look like it only
 * steered that one — and it did, while the ranking beside it silently reported a
 * calendar year and never moved. In a section header it steers what it says it
 * steers.
 */
export function PeriodeKiezer({
  months,
  period,
  onPeriod,
  label,
}: {
  months: string[];
  /** Active token: 'ALL' | 'YTD' | 'YYYY-MM'. */
  period: string;
  onPeriod: (p: string | null) => void;
  /** Spelled-out period, shown next to the buttons. */
  label?: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {label && <span style={{ fontSize: 12, color: '#999999' }}>{label}</span>}
      <button type="button" style={chipStyle(period === 'ALL')} onClick={() => onPeriod('ALL')}>
        Alle
      </button>
      <button type="button" style={chipStyle(period === 'YTD')} onClick={() => onPeriod('YTD')}>
        Dit jaar
      </button>
      {months.length > 0 && (
        <Dropdown
          value={period !== 'ALL' && period !== 'YTD' ? period : ''}
          onChange={(v) => onPeriod(v || null)}
          placeholder="Maand…"
          size="sm"
          ariaLabel="Filter op maand"
          options={monthOptions(months)}
        />
      )}
    </div>
  );
}
